import * as React from "react";
import { useTerms } from "@/hooks/useTerms";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import {
  AlertCircle,
  ArrowRight,
  Check,
  Download,
  Loader2,
  MoreHorizontal,
  Search,
  Trash2,
  UserMinus,
  UserPlus,
  X,
} from "lucide-react";
import type {
  AdmissionFunnelStage,
  BulkResultResponse,
  EnquiryListFilters,
  StaffRosterItem,
} from "@eduviaos/shared";
import {
  exportEnquiriesCsv,
  useBulkAdvance,
  useBulkAssign,
  useBulkDelete,
  useStaffRoster,
} from "@/hooks/useAdmissions";
import { summariseBulkResult } from "@/components/admissions/bulkResultSummary";

/**
 * BulkActionBar — Phase D4.
 *
 * Sticky toolbar that surfaces above the admissions DataTable whenever
 * the IA has selected ≥1 row. Models the four bulk affordances we ship
 * BE-side (Iron Rule #1 — every BE bulk endpoint has a UI):
 *
 *   1. Advance — drive every selected enquiry into a chosen LIVE stage.
 *      Terminal stages (admitted / rejected) are deliberately omitted
 *      because they need per-row metadata (admission_number /
 *      rejection_reason); the per-row Convert / Reject dialogs handle
 *      those one at a time.
 *   2. Assign — set or clear the primary owner across the selection.
 *      Reuses the staff roster from /admissions/staff so it never
 *      drifts from the per-row AssignmentMenu.
 *   3. Delete — soft-delete (move to bin) with a typed-confirmation
 *      dialog because the BE supports up to 200 IDs in a single call
 *      and a misclick across 200 rows is unrecoverable in a single
 *      undo.
 *   4. Export CSV — streams the *current filter set* (not the
 *      selection) because the IA's mental model is "give me a sheet of
 *      these enquiries" and selection gets cleared on every filter
 *      change. We surface that distinction in the menu copy so it's
 *      never surprising.
 *
 * Partial-success handling:
 *   The BE returns a per-item envelope ({ ok, error_code, error_message })
 *   with HTTP 200 even when some IDs failed (e.g. one was already
 *   converted, one is missing the active stage form).
 *
 *   - Full success → `toast.success` (page-level success, no recovery
 *     context to anchor on screen). The success rows clear out of the
 *     selection so the toolbar disappears.
 *   - Partial success or full failure → inline `<PartialResultBanner>`
 *     above the toolbar with up to 5 per-row failures (enquiry id +
 *     reason). The success rows are cleared so the remaining selection
 *     points at exactly the rows that still need attention. Inline is
 *     required because the failure list is structured recovery copy
 *     anchored to the selection — moving it to a global toast in the
 *     bottom-right would unanchor "this enquiry failed because X" from
 *     the still-selected row it refers to.
 *
 *   This decision rule is consistent with every prior E1.b tranche:
 *   page-level success → global toast; structured recovery copy → keep
 *   in-place.
 */

interface BulkActionBarProps {
  selectedIds: string[];
  onClear: () => void;
  /** Drop the IDs that succeeded after a partial-success result so the
   * remaining selection points at rows that still need attention. */
  onSucceededIds: (ids: string[]) => void;
  stages: AdmissionFunnelStage[];
  /** The currently-applied list filters, used by the CSV export so the
   * download mirrors what the IA can see on screen. */
  currentFilters: EnquiryListFilters;
}

export function BulkActionBar({
  selectedIds,
  onClear,
  onSucceededIds,
  stages,
  currentFilters,
}: BulkActionBarProps) {
  const count = selectedIds.length;
  const [openDialog, setOpenDialog] = React.useState<null | "advance" | "assign" | "delete">(null);
  const [partialBanner, setPartialBanner] = React.useState<PartialBannerState | null>(null);
  const [exporting, setExporting] = React.useState(false);

  // Reset the partial-failure banner when the IA *moves on* — selects
  // new rows or clears the selection — but NOT when the selection
  // shrinks to exactly the failing rows after a partial-success. The
  // post-result narrow is the FE's own `onSucceededIds(...)` auto-peel
  // and the banner is the still-relevant recovery copy for the
  // remaining rows; clobbering it on that same render breaks the
  // partial-success UX (latent since Phase D4). We snapshot the
  // expected post-result fingerprint at result time and skip the reset
  // for exactly that one transition.
  const selectionFingerprint = selectedIds.join(",");
  const expectedPostResultFingerprintRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (expectedPostResultFingerprintRef.current === selectionFingerprint) {
      expectedPostResultFingerprintRef.current = null;
      return;
    }
    setPartialBanner(null);
  }, [selectionFingerprint]);

  if (count === 0) return null;

  const handleResult = (result: BulkResultResponse, actionLabel: string): void => {
    const summary = summariseBulkResult(result, actionLabel);
    if (summary.kind === "all-ok") {
      toast.success(summary.toastMessage);
      setPartialBanner(null);
    } else {
      const succeededSet = new Set(summary.succeededIds);
      const expectedNarrowed = selectedIds.filter((id) => !succeededSet.has(id));
      expectedPostResultFingerprintRef.current = expectedNarrowed.join(",");
      setPartialBanner({
        successful: summary.successful,
        failed: summary.failed,
        failures: summary.failures,
        actionLabel,
      });
    }
    if (summary.succeededIds.length > 0) onSucceededIds(summary.succeededIds);
    setOpenDialog(null);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportEnquiriesCsv(currentFilters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const filename = `admissions-${new Date().toISOString().slice(0, 10)}.csv`;
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      const message = (e as { message?: string } | null)?.message ?? "Couldn't export — try again.";
      toast.error(message);
    } finally {
      setExporting(false);
    }
  };

  const liveStages = stages.filter((s) => !s.is_terminal);

  return (
    <>
      <div
        role="region"
        aria-label={`${count} ${count === 1 ? "enquiry" : "enquiries"} selected — bulk actions available`}
        className={cn(
          "border-primary/30 bg-primary/5 sticky top-0 z-10 flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3",
          "shadow-sm",
        )}
        data-testid="bulk-action-bar"
      >
        <div className="flex items-center gap-2">
          <span className="bg-primary text-primary-foreground inline-flex size-6 items-center justify-center rounded-full text-[11px] font-semibold tabular-nums">
            {count}
          </span>
          <span className="text-foreground text-sm font-medium">
            {count === 1 ? "enquiry selected" : "enquiries selected"}
          </span>
        </div>

        <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOpenDialog("advance")}
            disabled={liveStages.length === 0}
            data-testid="bulk-advance-trigger"
          >
            <ArrowRight className="size-3.5" aria-hidden="true" />
            Advance to…
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOpenDialog("assign")}
            data-testid="bulk-assign-trigger"
          >
            <UserPlus className="size-3.5" aria-hidden="true" />
            Assign…
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                aria-label="More bulk actions"
                data-testid="bulk-more-trigger"
              >
                <MoreHorizontal className="size-3.5" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[220px]">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider">
                Bulk
              </DropdownMenuLabel>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  void handleExport();
                }}
                disabled={exporting}
                data-testid="bulk-export-trigger"
              >
                {exporting ? (
                  <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
                ) : (
                  <Download className="size-3.5" aria-hidden="true" />
                )}
                Export current filters as CSV
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => setOpenDialog("delete")}
                className="text-destructive focus:text-destructive"
                data-testid="bulk-delete-trigger"
              >
                <Trash2 className="size-3.5" aria-hidden="true" />
                Move {count} to bin…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            aria-label="Clear selection"
            data-testid="bulk-clear"
          >
            <X className="size-3.5" aria-hidden="true" />
            Clear
          </Button>
        </div>
      </div>

      {partialBanner && (
        <PartialResultBanner banner={partialBanner} onDismiss={() => setPartialBanner(null)} />
      )}

      <BulkAdvanceDialog
        open={openDialog === "advance"}
        onOpenChange={(o) => !o && setOpenDialog(null)}
        selectedIds={selectedIds}
        liveStages={liveStages}
        onResult={(r) => handleResult(r, "advanced")}
      />

      <BulkAssignDialog
        open={openDialog === "assign"}
        onOpenChange={(o) => !o && setOpenDialog(null)}
        selectedIds={selectedIds}
        onResult={(r) => handleResult(r, "assigned")}
      />

      <BulkDeleteDialog
        open={openDialog === "delete"}
        onOpenChange={(o) => !o && setOpenDialog(null)}
        selectedIds={selectedIds}
        onResult={(r) => handleResult(r, "moved to bin")}
      />
    </>
  );
}

// ── Partial-result banner ───────────────────────────────────────────────────

interface PartialBannerState {
  successful: number;
  failed: number;
  failures: { enquiry_id: string; reason: string }[];
  actionLabel: string;
}

function PartialResultBanner({
  banner,
  onDismiss,
}: {
  banner: PartialBannerState;
  onDismiss: () => void;
}) {
  return (
    <div
      role="status"
      className={cn(
        "mt-2 rounded-md border px-3 py-2 text-xs",
        "border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-300",
      )}
      data-testid="bulk-partial-banner"
    >
      <div className="flex items-start gap-2">
        <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        <div className="flex-1">
          <div className="font-medium">
            {banner.successful} {banner.actionLabel}, {banner.failed} failed.
          </div>
          <ul className="mt-1.5 space-y-0.5 text-[11px]">
            {banner.failures.slice(0, 5).map((f) => (
              <li key={f.enquiry_id} className="flex gap-2">
                <span className="text-muted-foreground/80 tabular-nums">
                  {f.enquiry_id.slice(-6)}
                </span>
                <span>{f.reason}</span>
              </li>
            ))}
            {banner.failures.length > 5 && (
              <li className="text-muted-foreground">
                + {banner.failures.length - 5} more failures
              </li>
            )}
          </ul>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-auto px-2 py-0.5 text-xs"
          onClick={onDismiss}
        >
          Dismiss
        </Button>
      </div>
    </div>
  );
}

// ── Advance dialog ──────────────────────────────────────────────────────────

interface AdvanceDialogProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  selectedIds: string[];
  liveStages: AdmissionFunnelStage[];
  onResult: (r: BulkResultResponse) => void;
}

function BulkAdvanceDialog({
  open,
  onOpenChange,
  selectedIds,
  liveStages,
  onResult,
}: AdvanceDialogProps) {
  const mut = useBulkAdvance();
  const [stageKey, setStageKey] = React.useState<string>("");
  const [note, setNote] = React.useState<string>("");
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setStageKey(liveStages[0]?.stage_key ?? "");
      setNote("");
      setError(null);
    }
  }, [open, liveStages]);

  const submit = async () => {
    if (!stageKey) return;
    setError(null);
    try {
      const res = await mut.mutateAsync({
        enquiry_ids: selectedIds,
        to_stage_key: stageKey,
        note: note.trim() || null,
      });
      onResult(res);
    } catch (e) {
      setError((e as { message?: string } | null)?.message ?? "Couldn't advance — try again.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Advance {selectedIds.length} enquir{selectedIds.length === 1 ? "y" : "ies"}
          </DialogTitle>
          <DialogDescription>
            Each selected enquiry will move to the chosen stage. Rows that require a stage form
            completion before this stage will fail with an explanation — you can retry them
            individually.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="bulk-advance-stage">Move to stage</Label>
            <Select value={stageKey} onValueChange={setStageKey}>
              <SelectTrigger id="bulk-advance-stage" data-testid="bulk-advance-stage">
                <SelectValue placeholder="Pick a stage" />
              </SelectTrigger>
              <SelectContent>
                {liveStages.map((s) => (
                  <SelectItem key={s.stage_key} value={s.stage_key}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bulk-advance-note">
              Note{" "}
              <span className="text-muted-foreground font-normal">
                (optional, logged on each row)
              </span>
            </Label>
            <Textarea
              id="bulk-advance-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="e.g. Walk-ins from this Saturday's open day"
            />
          </div>
          {error && (
            <p role="alert" className="text-destructive text-xs">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">Cancel</Button>
          </DialogClose>
          <Button
            onClick={() => void submit()}
            disabled={!stageKey || mut.isPending}
            data-testid="bulk-advance-submit"
          >
            {mut.isPending && <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />}
            Advance
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Assign dialog ───────────────────────────────────────────────────────────

interface AssignDialogProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  selectedIds: string[];
  onResult: (r: BulkResultResponse) => void;
}

function BulkAssignDialog({ open, onOpenChange, selectedIds, onResult }: AssignDialogProps) {
  const { ct } = useTerms();
  const mut = useBulkAssign();
  const { data: roster, isLoading } = useStaffRoster();
  const [picked, setPicked] = React.useState<StaffRosterItem | null>(null);
  const [unassign, setUnassign] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [note, setNote] = React.useState<string>("");
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setPicked(null);
      setUnassign(false);
      setQuery("");
      setNote("");
      setError(null);
    }
  }, [open]);

  const items = React.useMemo(() => roster?.items ?? [], [roster?.items]);
  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((s) => {
      const name = (s.full_name ?? "").toLowerCase();
      return name.includes(q) || s.email.toLowerCase().includes(q);
    });
  }, [items, query]);

  const submit = async () => {
    setError(null);
    if (!unassign && !picked) {
      setError(ct("Pick a staff member or choose Unassign."));
      return;
    }
    try {
      const res = await mut.mutateAsync({
        enquiry_ids: selectedIds,
        assigned_to: unassign ? null : picked!.user_id,
        note: note.trim() || null,
      });
      onResult(res);
    } catch (e) {
      setError((e as { message?: string } | null)?.message ?? "Couldn't assign — try again.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[480px]">
        <DialogHeader>
          <DialogTitle>
            Assign {selectedIds.length} enquir{selectedIds.length === 1 ? "y" : "ies"}
          </DialogTitle>
          <DialogDescription>
            {ct(
              "Sets the primary owner across the selection. Pick a staff member or unassign all.",
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <button
            type="button"
            onClick={() => {
              setUnassign(true);
              setPicked(null);
            }}
            className={cn(
              "border-border/60 hover:bg-muted/40 flex w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-left text-xs transition-colors",
              unassign && "border-primary/40 bg-primary/5",
            )}
            data-testid="bulk-assign-unassign"
          >
            <span className="flex items-center gap-2">
              <UserMinus className="text-muted-foreground size-3.5" aria-hidden="true" />
              Unassign — clear the owner across the selection
            </span>
            {unassign && <Check className="text-primary size-3.5" aria-hidden="true" />}
          </button>
          <div>
            <div className="relative">
              <Search
                className="text-muted-foreground absolute left-2 top-1/2 size-3.5 -translate-y-1/2"
                aria-hidden="true"
              />
              <Input
                type="search"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setUnassign(false);
                }}
                placeholder={ct("Search staff…")}
                className="h-8 pl-7 text-xs"
                aria-label={ct("Search staff")}
              />
            </div>
            <div className="mt-2 max-h-[200px] overflow-y-auto rounded-md border">
              {isLoading ? (
                <div className="text-muted-foreground flex items-center justify-center gap-2 py-6 text-xs">
                  <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
                  {ct("Loading staff…")}
                </div>
              ) : filtered.length === 0 ? (
                <div className="text-muted-foreground px-3 py-6 text-center text-xs">
                  {query.trim() ? ct(`No staff match "${query}".`) : ct("No assignable staff yet.")}
                </div>
              ) : (
                <ul role="listbox" aria-label={ct("Pick a staff member")}>
                  {filtered.map((staff) => {
                    const active = picked?.user_id === staff.user_id;
                    return (
                      <li key={staff.user_id}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={active}
                          onClick={() => {
                            setPicked(staff);
                            setUnassign(false);
                          }}
                          className={cn(
                            "hover:bg-muted/40 flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs",
                            active && "bg-primary/10 text-primary-foreground",
                          )}
                          data-testid={`bulk-assign-staff-${staff.user_id}`}
                        >
                          <span className="min-w-0 truncate">
                            <span className="text-foreground block truncate">
                              {staff.full_name?.trim() || staff.email}
                            </span>
                            <span className="text-muted-foreground block truncate text-[10px]">
                              {staff.email} · {staff.role}
                            </span>
                          </span>
                          {active && (
                            <Check className="text-primary size-3.5 shrink-0" aria-hidden="true" />
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bulk-assign-note">
              Note <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Textarea
              id="bulk-assign-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              rows={2}
              placeholder="e.g. Reassigning Saturday walk-ins to Priya"
            />
          </div>
          {error && (
            <p role="alert" className="text-destructive text-xs">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">Cancel</Button>
          </DialogClose>
          <Button
            onClick={() => void submit()}
            disabled={(!picked && !unassign) || mut.isPending}
            data-testid="bulk-assign-submit"
          >
            {mut.isPending && <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />}
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete dialog ───────────────────────────────────────────────────────────

interface DeleteDialogProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  selectedIds: string[];
  onResult: (r: BulkResultResponse) => void;
}

function BulkDeleteDialog({ open, onOpenChange, selectedIds, onResult }: DeleteDialogProps) {
  const mut = useBulkDelete();
  const expected = `Delete ${selectedIds.length}`;
  const [confirm, setConfirm] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setConfirm("");
      setError(null);
    }
  }, [open]);

  const submit = async () => {
    setError(null);
    if (confirm !== expected) {
      setError(`Type "${expected}" exactly to confirm.`);
      return;
    }
    try {
      const res = await mut.mutateAsync({ enquiry_ids: selectedIds });
      onResult(res);
    } catch (e) {
      setError((e as { message?: string } | null)?.message ?? "Couldn't delete — try again.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-destructive">
            Move {selectedIds.length} enquir{selectedIds.length === 1 ? "y" : "ies"} to the bin
          </DialogTitle>
          <DialogDescription>
            Soft-deleted enquiries are hidden from the funnel but kept for 30 days in the bin where
            they can be restored. After 30 days they are permanently purged for compliance.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="bulk-delete-confirm">
              Type <code className="bg-muted rounded px-1">{expected}</code> to confirm
            </Label>
            <Input
              id="bulk-delete-confirm"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="off"
              data-testid="bulk-delete-confirm"
            />
          </div>
          {error && (
            <p role="alert" className="text-destructive text-xs">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">Cancel</Button>
          </DialogClose>
          <Button
            variant="destructive"
            onClick={() => void submit()}
            disabled={confirm !== expected || mut.isPending}
            data-testid="bulk-delete-submit"
          >
            {mut.isPending && <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />}
            Move to bin
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
